import { CheckCircle2, FileSignature, Loader2, RotateCw } from 'lucide-react'
import { Button } from '@/shared/ui/primitives'
import { MinorWarningNotice } from './minor-warning-notice'
import { WaiverSignatureStep } from './waiver-signature-step'

export type LiabilityWaiverSectionProps = {
  isLoadingRequired: boolean
  requiredCount: number
  signedCount: number
  currentTemplateName?: string
  currentEmbedUrl?: string
  isSigning: boolean
  isStartingSignature: boolean
  isAwaitingCompletion: boolean
  isPolling: boolean
  isWaitingLong: boolean
  isCheckingAgain: boolean
  isCheckAgainPending: boolean
  allSigned: boolean
  error?: string
  sandbox: string
  isMinor: boolean
  age?: number
  startSigning: () => void
  reopen: () => void
  dismiss: () => void
  checkAgain: () => void
}

export function LiabilityWaiverSection({
  isLoadingRequired,
  requiredCount,
  signedCount,
  currentTemplateName,
  currentEmbedUrl,
  isSigning,
  isStartingSignature,
  isAwaitingCompletion,
  isPolling,
  isWaitingLong,
  isCheckingAgain,
  isCheckAgainPending,
  allSigned,
  error,
  sandbox,
  isMinor,
  age,
  startSigning,
  reopen,
  dismiss,
  checkAgain,
}: LiabilityWaiverSectionProps) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        This organization requires all users to sign the documents below before joining.
      </p>

      {isMinor ? <MinorWarningNotice age={age} /> : null}

      {isLoadingRequired ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading required waivers…
        </div>
      ) : null}

      {!isLoadingRequired && requiredCount > 0 ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Waivers signed
            </span>
            <span className="text-sm text-muted-foreground">
              {signedCount} of {requiredCount}
            </span>
          </div>
        </div>
      ) : null}

      {allSigned ? (
        <div className="flex items-center gap-2 text-sm text-sage">
          <CheckCircle2 className="size-4" aria-hidden />
          All required waivers are signed.
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {!allSigned && !isSigning && !isAwaitingCompletion ? (
        <Button
          type="button"
          variant="default"
          onClick={startSigning}
          disabled={isLoadingRequired || requiredCount === 0 || isStartingSignature}
        >
          {isStartingSignature ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Opening waiver…
            </>
          ) : (
            <>
              <FileSignature className="size-4" />
              Sign waiver
            </>
          )}
        </Button>
      ) : null}

      {isAwaitingCompletion ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          {isWaitingLong ? (
            <p className="text-sm text-muted-foreground">
              Finishing up your signature… this can take a minute.
            </p>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Waiting for your signature to be confirmed…
            </div>
          )}
          {isWaitingLong && isCheckAgainPending ? (
            <p className="text-sm text-muted-foreground" role="status">
              Not confirmed yet — we&apos;ll keep checking.
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            {isWaitingLong ? (
              <Button
                type="button"
                variant="outline"
                onClick={checkAgain}
                disabled={isCheckingAgain}
              >
                {isCheckingAgain ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Checking…
                  </>
                ) : (
                  <>
                    <RotateCw className="size-4" />
                    Check again
                  </>
                )}
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={reopen}>
              <FileSignature className="size-4" />
              Re-open waiver
            </Button>
          </div>
        </div>
      ) : null}

      {isSigning && currentEmbedUrl ? (
        <WaiverSignatureStep
          embedUrl={currentEmbedUrl}
          sandbox={sandbox}
          templateName={currentTemplateName}
          isPolling={isPolling}
          onClose={dismiss}
        />
      ) : null}
    </div>
  )
}
